import React, {useEffect, useMemo} from 'react';
import {Grid, FormControl, InputLabel, Select, MenuItem} from '@material-ui/core';
import {observer} from 'mobx-react-lite';

import {TextBox} from '../../../Core/UI';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {Approval} from '../../../Core/mobx/ParameterStore/Approval';
import {SelectInputElement} from '../../PreQualification/Components/SelectInputElement';
import {CategoryCard} from '../CategoryCard';
import RootStore from '../../../Core/mobx/RootStore';
import {fieldOptions} from '@/Core/mobx/ParameterStore/ApprovalXref';
import ProductSelector from './ProductSelector';
import {PIVOT_REASONS} from '../../../Core/constants/pivotReasons';

const otherReasons = [
    'OtherReason',
    'OtherOtherReason',
    'EligibilityOtherReason',
    'ClientRequestedOtherReason',
    'CapabilityQuotingTechIssue',
    'CapabilityOtherReason',
];

type AgentInfoProps = {
    valueChanged: (p?: Parameter, value?: any) => void;
    parameters: Approval;
    setCategory: (entry: string) => void;
    index: number;
};

const AgentInfo = observer(({valueChanged, parameters, setCategory, index}: AgentInfoProps) => {
    const required = (value?: string) => (
        <span>
            {value && value} <span style={{color: 'red'}}>*</span>
        </span>
    );

    // Check if we should show pivot reason (when selected products don't match recommended)
    const showPivotReason = useMemo(() => {
        // Use agentProductCodes for comparison since recommendedProductCode is also a product code
        const selectedProductCodes = parameters.agentProductCodes?.value || [];
        const recommendedCode = parameters.recommendedProductCode?.value;

        // For multi-select, check if the recommended product is NOT in the selected products
        const shouldShow =
            recommendedCode && selectedProductCodes.length > 0 && !selectedProductCodes.includes(recommendedCode);
        console.log('AgentInfo showPivotReason:', {
            selectedProductCodes,
            recommendedCode,
            shouldShow,
        });
        return shouldShow;
    }, [parameters.agentProductCodes?.value, parameters.recommendedProductCode?.value]);

    // Get pivot reason options based on the recommended product
    const currentPivotOptions = useMemo(() => {
        const recommended = parameters.recommendedProductCode?.value || '';
        const options = PIVOT_REASONS[recommended] || PIVOT_REASONS['default'];
        return options;
    }, [parameters.recommendedProductCode?.value]);

    useEffect(() => {
        if (!parameters.agentProducts.value.length || parameters.agentProducts.value.includes('Prosper')) {
            valueChanged(parameters.prosperNotChosenReason, '');
            valueChanged(parameters.prosperNotChosenDetails, '');
        }
        if (!parameters.agentProducts.value.length || parameters.agentProducts.value.includes('FGEverlast')) {
            valueChanged(parameters.fgEverlastNotChosenReason, '');
        }
    }, [parameters.agentProducts.value]);

    useEffect(() => {
        if (
            !parameters.agentProducts.value.includes('Prosper') &&
            !otherReasons.includes(parameters.prosperNotChosenReason.value)
        ) {
            valueChanged(parameters.prosperNotChosenDetails, '');
        }
    }, [parameters.agentProducts.value, parameters.prosperNotChosenReason.value]);

    const filteredOptions = useMemo(
        () =>
            parameters.prosperNotChosenReason.value === 'ClientrequestedHighercapoptionotherthanSPother'
                ? fieldOptions.prosperNotChosenReason.options
                : fieldOptions.prosperNotChosenReason.options.filter(
                      (item) => item !== 'Client requested (Higher cap, option other than S&P, other)',
                  ),
        [parameters.prosperNotChosenReason.value],
    );

    return (
        <CategoryCard title={'Agent & Product Information'} setCategory={setCategory} index={index}>
            <Grid item xs={4}>
                <TextBox
                    id={'agentName'}
                    label={'Agent Name'}
                    value={parameters.agentName?.value}
                    onChange={(value) => valueChanged(parameters.agentName, value)}
                />
            </Grid>
            <Grid item xs={4}>
                <SelectInputElement
                    param={{
                        label: 'Transfer Owner',
                        name: 'bdsName',
                        options: RootStore.agentsList,
                        ...parameters.bdsName,
                    }}
                    required={''}
                    disabled={!RootStore.agentsList.length}
                    valueChanged={valueChanged}
                />
            </Grid>
            <Grid item xs={4}>
                <ProductSelector
                    param={{
                        label: 'Product(s)',
                        name: 'agentProducts',
                        value: parameters.agentProductCodes?.value || parameters.agentProducts?.value,
                        setValue: (v: any) => {
                            // v contains product codes, we need to set both fields
                            // Set agentProducts with legacy keys
                            if (parameters.agentProducts?.setValue) {
                                // This will be handled by ProductSelector's onChange which converts to legacy keys
                                parameters.agentProducts.setValue(v);
                            }
                            // The ProductSelector will handle setting agentProductCodes in its onChange
                        },
                    }}
                    required={''}
                    valueChanged={valueChanged}
                    multiple
                    useApprovalProducts={true}
                />
            </Grid>
            {showPivotReason && (
                <>
                    <Grid item xs={6}>
                        <FormControl fullWidth>
                            <InputLabel shrink>
                                Pivot Reason <span style={{color: 'red'}}>*</span>
                            </InputLabel>
                            <Select
                                value={parameters.preapprovalPivotReason?.value || ''}
                                onChange={(e) => {
                                    const newValue = e.target.value as string;
                                    valueChanged(parameters.preapprovalPivotReason, newValue);
                                    // Clear the other reason if switching to a reason that doesn't need it
                                    const selectedOption = currentPivotOptions.find((opt) => opt.text === newValue);
                                    if (
                                        !selectedOption?.requiresExplanation &&
                                        parameters.preapprovalPivotOtherReason
                                    ) {
                                        valueChanged(parameters.preapprovalPivotOtherReason, '');
                                    }
                                }}
                                displayEmpty
                            >
                                <MenuItem value="">
                                    <em>Select a reason</em>
                                </MenuItem>
                                {currentPivotOptions.map((option) => (
                                    <MenuItem key={option.text} value={option.text}>
                                        {option.text}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {currentPivotOptions.find((opt) => opt.text === parameters.preapprovalPivotReason?.value)
                        ?.requiresExplanation && (
                        <Grid item xs={6}>
                            <TextBox
                                id="preapprovalPivotOtherReason"
                                label={required('Please explain')}
                                value={parameters.preapprovalPivotOtherReason?.value}
                                onChange={(value) => valueChanged(parameters.preapprovalPivotOtherReason, value)}
                            />
                        </Grid>
                    )}
                </>
            )}
        </CategoryCard>
    );
});

export default AgentInfo;
